import { useEffect, useRef } from "react";
import { industries } from "@/mocks/industries";
import { Link } from "react-router-dom";

export default function Industries() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll(".reveal");
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="industries" ref={sectionRef} className="py-20 md:py-28 lg:py-32 bg-background-50">
      <div className="px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <span className="reveal text-accent-500 text-xs font-semibold tracking-[0.25em] uppercase mb-4 block">
              Who We Serve
            </span>
            <h2 className="reveal font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              Industries We <span className="text-primary-600">Protect</span>
            </h2>
            <p className="reveal text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Specialized security solutions tailored to the unique needs of each industry we serve.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {industries.map((industry, index) => (
              <div
                key={industry.id}
                className="reveal group bg-background-100 rounded-xl p-6 border border-background-200/70 hover:border-accent-300 hover:bg-accent-50/50 transition-all duration-500"
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-lg group-hover:bg-primary-500 transition-colors duration-500">
                    <i className={`${industry.icon} text-primary-600 text-xl group-hover:text-background-50 transition-colors duration-500`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-1">
                      {industry.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {industry.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="reveal mt-16 bg-primary-500 rounded-2xl p-8 md:p-12 text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
              Don&apos;t See Your Industry?
            </h3>
            <p className="text-background-50/80 text-base max-w-xl mx-auto mb-6 leading-relaxed">
              We provide customized security solutions for a wide range of industries.
              Contact us to discuss how we can protect your specific business needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold px-8 py-3.5 rounded-md transition-all duration-300 whitespace-nowrap"
            >
              Contact Us Today
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}